import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, User as UserIcon, Trophy, XCircle, Ban } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RunnerCard } from '@/components/runners/RunnerCard';
import { RunnerStatsModal } from '@/components/runners/RunnerStatsModal';
import { ScoreBadge } from '@/features/races/detail/ScoreBadge';
import { getMedalStyle } from '@/lib/medals';
import type { GetRaceDetailsQuery } from '@/gql/graphql';

type RaceRunner = NonNullable<NonNullable<GetRaceDetailsQuery['race']>['runners']>[number];

type BadgeMode = 'expectedRank' | 'betPosition' | 'finalResult';

interface RunnerListProps {
    runners: RaceRunner[];
    canManage: boolean;
    raceFormat?: string | null;
    onDeleteRunner: (runner: RaceRunner) => void;
    isFiltered?: boolean;
    expectedRankMap?: Map<string, number>;
    userBetRankings?: Map<string, number> | null;
    /** Map of runnerId → { score, tokens } for scored bets */
    userBetScores?: Map<string, { score: number; tokens: number }> | null;
    useScratchResult?: boolean;
    badgeMode: BadgeMode;
}

export function RunnerList({ runners, canManage, raceFormat, onDeleteRunner, isFiltered, expectedRankMap, userBetRankings, userBetScores, useScratchResult, badgeMode }: RunnerListProps) {
    const { t } = useTranslation('races');
    const [selectedRunner, setSelectedRunner] = useState<RaceRunner | null>(null);

    if (runners.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                <UserIcon size={32} className="mb-2 opacity-20" />
                <p className="text-sm font-medium">
                    {isFiltered ? t('details.filter.noResults') : t('details.emptyRunners')}
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="grid gap-3">
                {runners.map((runner) => {
                    const badge = renderBadge(runner, badgeMode, expectedRankMap, userBetRankings, useScratchResult);
                    const isBetRunner = runner.id ? (userBetRankings?.has(runner.id) ?? false) : false;
                    const betScore = runner.id ? userBetScores?.get(runner.id) : undefined;

                    return (
                        <RunnerCard
                            key={runner.id}
                            runner={runner}
                            isHighlight={isBetRunner}
                            onClick={() => setSelectedRunner(runner)}
                            className={cn(
                                "border-0 shadow-sm ring-1 transition-all",
                                isBetRunner ? "ring-emerald-200 bg-emerald-50/30" : "ring-slate-100 hover:ring-emerald-200"
                            )}
                            action={
                                <div className="flex items-center gap-1.5">
                                    {betScore && <ScoreBadge score={betScore.score} />}
                                    {betScore && betScore.tokens > 1 && (
                                        <span className="px-1 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-700">x2</span>
                                    )}
                                    {badge}
                                    {canManage && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onDeleteRunner(runner); }}
                                            className="p-1.5 text-slate-200 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                            }
                        />
                    );
                })}
            </div>

            {selectedRunner && (
                <RunnerStatsModal
                    runner={selectedRunner}
                    raceFormat={raceFormat}
                    onClose={() => setSelectedRunner(null)}
                />
            )}
        </>
    );
}

// ─── Badge rendering ───

function renderBadge(
    runner: RaceRunner,
    mode: BadgeMode,
    expectedRankMap?: Map<string, number>,
    userBetRankings?: Map<string, number> | null,
    useScratchResult?: boolean,
): React.ReactNode {
    if (mode === 'finalResult') {
        return renderFinalResultBadge(runner, useScratchResult);
    }

    const rank = runner.id ? expectedRankMap?.get(runner.id) : undefined;

    if (mode === 'betPosition' && userBetRankings && runner.id) {
        const betPos = userBetRankings.get(runner.id);
        if (betPos != null) {
            return renderDualBadge(rank, betPos);
        }
    }

    return renderExpectedRankBadge(rank);
}

function renderExpectedRankBadge(rank?: number): React.ReactNode {
    if (rank == null) return null;
    return (
        <span
            className="w-8 h-8 rounded-lg bg-slate-100 text-slate-400 text-xs font-bold flex items-center justify-center flex-shrink-0"
            title="Rang attendu"
        >
            {rank}
        </span>
    );
}

function renderDualBadge(expectedRank: number | undefined, betPosition: number): React.ReactNode {
    const betLabel = betPosition === 0 ? 'DNF' : `${betPosition}`;
    const betColor = betPosition === 0
        ? "text-amber-600"
        : "text-emerald-700";

    return (
        <div className="flex items-center gap-1 flex-shrink-0" title="Rang attendu → Ta prédiction">
            {expectedRank != null && (
                <span className="text-[11px] font-bold text-slate-300">{expectedRank}</span>
            )}
            <span className="text-[10px] text-slate-300">→</span>
            <span className={`w-7 h-7 rounded-lg text-xs font-black flex items-center justify-center ${betPosition === 0 ? 'bg-amber-100 px-1' : 'bg-emerald-100'} ${betColor}`}>
                {betLabel}
            </span>
        </div>
    );
}

function renderFinalResultBadge(runner: RaceRunner, useScratchResult?: boolean): React.ReactNode {
    const result = useScratchResult ? (runner.scratchResult ?? runner.result) : runner.result;

    if (result == null) {
        return (
            <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-300 text-xs font-bold flex items-center justify-center flex-shrink-0">
                —
            </span>
        );
    }

    if (result === -1) {
        return (
            <span
                className="h-8 px-2 rounded-lg bg-slate-200 text-slate-500 text-[10px] font-black flex items-center gap-1 justify-center flex-shrink-0"
                title="DNS"
            >
                <Ban size={10} /> DNS
            </span>
        );
    }

    if (result === 0) {
        return (
            <span
                className="h-8 px-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-black flex items-center gap-1 justify-center flex-shrink-0"
                title="DNF"
            >
                <XCircle size={10} /> DNF
            </span>
        );
    }

    // Podium: gold/silver/bronze for top 3
    if (result <= 3) {
        return (
            <span
                className={cn("w-8 h-8 rounded-lg text-xs font-black flex items-center gap-0.5 justify-center flex-shrink-0", getMedalStyle(result))}
                title={`#${result}`}
            >
                <Trophy size={10} /> {result}
            </span>
        );
    }

    return (
        <span
            className="w-8 h-8 rounded-lg bg-emerald-600 text-white text-xs font-black flex items-center gap-0.5 justify-center flex-shrink-0"
            title={`#${result}`}
        >
            {result}
        </span>
    );
}
